import { ApiStatus, ContractServices } from 'network'
import { isCryptoExchange } from 'platform-config'
import { requestIdleCallbackFrame } from 'utils'
import { SvIds } from '../constants/static-table-sv-id'
import { StorageIndexDBEnum } from '../storage/StorageDB'
import { useBaseInfoStore } from './use-base-info'
import { useExchangeInfoStore } from './use-exchange-info'
import { useStaticTableDataStore } from './use-static-table-data'
import { useUserInfoStore } from './use-user-info'

export const useDoubleContractAssetsAccountStore = defineStore('doubleContractAssetsAccount', () => {
  const doubleContractAssetsAccount = ref<ApiResponse.DoubleContractAssetsAccount>()
  const baseInfoStore = useBaseInfoStore()
  const staticTableDataStore = useStaticTableDataStore()
  const exchangeStore = useExchangeInfoStore()
  const userInfoStore = useUserInfoStore()
  const usdtItemId = ref(10007)
  const loading = ref(false)
  const timeout = ref<NodeJS.Timeout | null>(null)
  const hideOtherOrder = ref(false)
  const initLoading = ref(true)
  const openContractFeature = ref(window.localStorage.getItem(StorageIndexDBEnum.ExchangeOpenContractFeature) || '0')

  const stopFetchData = () => {
    if (timeout.value) {
      clearTimeout(timeout.value)
      timeout.value = null
    }
  }

  const fetchData = async () => {
    if (!userInfoStore.isLogin && typeof userInfoStore.isLogin === 'boolean') {
      initLoading.value = false
      return
    }
    if (!isCryptoExchange) return
    if (loading.value) return
    loading.value = true
    stopFetchData()
    const res = await ContractServices.getDoubleContractAssetsAccount()
    initLoading.value = false
    if (res.status === ApiStatus.Success) {
      if (res.data.openFutures) {
        openContractFeature.value = '1'
        window.localStorage.setItem(StorageIndexDBEnum.ExchangeOpenContractFeature, '1')
      }
      doubleContractAssetsAccount.value = res.data
      if (isCryptoExchange) {
        timeout.value = setTimeout(() => {
          requestIdleCallbackFrame(() => {
            fetchData()
          })
        }, 2000)
      }
    }
    loading.value = false
  }

  const positions = computed(() => {
    const list = doubleContractAssetsAccount.value?.positions.filter((item) => item.positionId !== 0) || []

    return list
  })

  const allPositions = computed(() => {
    return doubleContractAssetsAccount.value?.positions || []
  })

  const assets = computed(() => {
    return doubleContractAssetsAccount.value?.assets || []
  })

  const getCoinIconBySymbolId = (symbolId: SymbolId) => {
    const staticData = staticTableDataStore.staticTableDataMap[SvIds.ContractStaticSymbol]
    if (!staticData) return ''
    const data = staticData[symbolId]
    if (!data) return ''
    const openItemId = data?.openItemId
    const coinUrl = baseInfoStore.getCoinInfoByItemId(openItemId)?.itemDetail.coinUrl
    return coinUrl
  }

  const getSettleCoinNameBySymbolId = (symbolId: SymbolId) => {
    const staticData = staticTableDataStore.staticTableDataMap[SvIds.ContractStaticSymbol]
    if (!staticData) return ''
    const data = staticData[symbolId]
    if (!data) return ''
    const settlementItemId = data?.settlementItemId
    const settleName = staticTableDataStore.getCoinNameByItemId(settlementItemId)
    return settleName
  }

  const getCloseInterest = (position: ApiResponse.DoubleContractPosition | ApiResponse.ClosePosition) => {
    const config = exchangeStore.getContractSymbolInfoBySymbolId(position.symbolId)
    if ((position as ApiResponse.DoubleContractPosition).closeQty) {
      return (position as ApiResponse.DoubleContractPosition).closeQty.multiplyStr(config?.contractValue).removeTrailingZero
    }

    return (position as ApiResponse.ClosePosition).rlzCloseQty.multiplyStr(config?.contractValue).removeTrailingZero
  }

  const refresh = async () => {
    await fetchData()
  }

  return {
    fetchData,
    positions,
    getCoinIconBySymbolId,
    getSettleCoinNameBySymbolId,
    doubleContractAssetsAccount,
    refresh,
    assets,
    usdtItemId,
    hideOtherOrder,
    getCloseInterest,
    initLoading,
    stopFetchData,
    allPositions,
    openContractFeature,
  }
})
